import { useEffect, useMemo, useState } from 'react';
import { Button, Card, ChecklistItem, Icon, IconButton, ProgressRing, Tag } from '../ds';
import { EmptyState, Eyebrow, Screen, ScreenTitle } from '../components/primitives';
import { Sheet } from '../components/Sheet';
import { useToast } from '../components/Toast';
import * as api from '../lib/db';
import { useFamily } from '../state/FamilyContext';
import { addDays, dayContextLabel, dayKey, formatDateShortJa, parseISODate } from '../lib/time';
import type { PrepDay, PrepItem } from '../lib/types';

/**
 * じゅんび / Prep — the kindergarten checklist.
 *
 * A checklist belongs to the day the bag is for: towel and diapers get ticked
 * the evening before, the bento the morning of, all in the same document.
 * The screen opens on the default target day (today until noon, tomorrow
 * after) and the banner's arrows walk day by day in either direction —
 * weekends included, because pool bags are prep too. Ticking an item records
 * who packed it, which is the whole point when two people are splitting the
 * bag. Items themselves are editable, and archiving rather than deleting
 * keeps past days' history intact.
 */

const ICON_CHOICES = [
  'Backpack',
  'Utensils',
  'Droplets',
  'HardHat',
  'Baby',
  'Shirt',
  'Footprints',
  'BookOpen',
  'Umbrella',
  'Pencil',
  'Package',
];

export function PrepScreen() {
  const { familyId, me, children, prepItems, prepDay: defaultDay, prepDayKey, today, members } =
    useFamily();
  const toast = useToast();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  /** Days away from the default target day. 0 follows the automatic rollover. */
  const [dayOffset, setDayOffset] = useState(0);
  const selectedDate = useMemo(
    () => addDays(parseISODate(prepDayKey), dayOffset),
    [prepDayKey, dayOffset],
  );
  const selectedKey = dayKey(selectedDate);

  /**
   * The context only subscribes to the default day (that is what the home
   * card shows); walking to any other day opens its own subscription here.
   */
  const [offDay, setOffDay] = useState<PrepDay | null>(null);
  useEffect(() => {
    if (dayOffset === 0 || !familyId) {
      setOffDay(null);
      return;
    }
    setOffDay(null);
    return api.watchPrepDay(familyId, selectedKey, setOffDay, () =>
      setOffDay({ id: selectedKey, checks: {} }),
    );
  }, [familyId, selectedKey, dayOffset]);

  const prepDay = dayOffset === 0 ? defaultDay : (offDay ?? { id: selectedKey, checks: {} });

  const kid = children.find((c) => c.kind === 'kid') ?? null;
  const memberByUid = useMemo(() => new Map(members.map((m) => [m.uid, m])), [members]);

  const items = useMemo(
    () => prepItems.filter((p) => !kid || !p.childId || p.childId === kid.id),
    [prepItems, kid],
  );

  const done = items.filter((item) => prepDay.checks[item.id]).length;
  const total = items.length;

  const toggle = async (item: PrepItem, next: boolean) => {
    if (!familyId || !me) return;
    await api.setPrepChecked(familyId, selectedKey, item, me, next);
  };

  /** きょう · Today / あした · Tomorrow / きのう · Yesterday, else the weekday. */
  const dayContext = dayContextLabel(selectedDate, parseISODate(today));

  return (
    <Screen>
      <ScreenTitle
        eyebrow={kid ? `Kindergarten · ${kid.name}` : 'Kindergarten'}
        title="通園準備"
        action={
          total > 0 ? (
            <ProgressRing value={done} max={total} size={56} thickness={7} label={`${done}/${total}`} />
          ) : undefined
        }
      />

      <Card
        padding="sm"
        style={{
          marginBottom: 16,
          background: dayOffset === 0 ? 'var(--brand-subtle)' : 'var(--surface-sunken)',
          borderColor: 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconButton
            icon="ChevronLeft"
            label="前の日"
            variant="ghost"
            size="sm"
            onClick={() => setDayOffset((o) => o - 1)}
          />
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', flex: 1, minWidth: 0, textAlign: 'center' }}>
            {formatDateShortJa(selectedDate)}の準備
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{dayContext}</div>
          </div>
          <IconButton
            icon="ChevronRight"
            label="次の日"
            variant="ghost"
            size="sm"
            onClick={() => setDayOffset((o) => o + 1)}
          />
          {total > 0 ? (
            <IconButton
              icon={editing ? 'Check' : 'Pencil'}
              label={editing ? '編集をおわる' : 'リストを編集'}
              variant="ghost"
              size="sm"
              onClick={() => setEditing((v) => !v)}
            />
          ) : null}
        </div>
      </Card>

      {total > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item) => {
            const check = prepDay.checks[item.id];
            const packer = check ? memberByUid.get(check.byUid) : null;

            // Once packed, the meta line says who did it — that is the handoff
            // signal. Before that, it carries the English name and any hint.
            const meta = check
              ? `${item.labelEn} · ${packer?.name ?? (check.byRole === 'mom' ? 'ママ' : 'パパ')}が準備`
              : [item.labelEn, item.note].filter(Boolean).join(' · ');

            return (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ChecklistItem
                    icon={item.icon}
                    label={item.label}
                    meta={meta}
                    checked={!!check}
                    onChange={(next) => void toggle(item, next)}
                  />
                </div>
                {editing ? (
                  <IconButton
                    icon="Trash2"
                    label={`${item.label}をリストから外す`}
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (!familyId) return;
                      await api.archivePrepItem(familyId, item.id);
                      toast.show({ title: 'リストから外しました', message: item.label });
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <Card padding="md">
          <EmptyState icon={<Icon name="Backpack" size={24} />}>
            まだ持ち物がありません。
            <br />
            最初のひとつを追加しましょう。
          </EmptyState>
        </Card>
      )}

      <Button
        variant="outline"
        fullWidth
        iconStart="Plus"
        style={{ marginTop: 16 }}
        onClick={() => setAdding(true)}
      >
        項目を追加 · Add an item
      </Button>

      {done === total && total > 0 ? (
        <div
          className="cjk"
          style={{
            marginTop: 14,
            textAlign: 'center',
            fontSize: 'var(--text-sm)',
            color: 'var(--sage-600)',
            fontWeight: 700,
          }}
        >
          ぜんぶ準備できました。おつかれさま。
        </div>
      ) : null}

      <AddPrepItemSheet
        open={adding}
        onClose={() => setAdding(false)}
        nextOrder={items.length}
        childId={kid?.id ?? ''}
        onSave={async (draft) => {
          if (!familyId || !me) return;
          await api.addPrepItem(familyId, me, draft);
          toast.show({ title: 'リストに追加しました', message: draft.label });
          setAdding(false);
        }}
      />
    </Screen>
  );
}

function AddPrepItemSheet({
  open,
  onClose,
  onSave,
  nextOrder,
  childId,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (draft: Omit<PrepItem, 'id' | 'archived'>) => Promise<void>;
  nextOrder: number;
  childId: string;
}) {
  const [label, setLabel] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [note, setNote] = useState('');
  const [icon, setIcon] = useState('Package');
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setLabel('');
    setLabelEn('');
    setNote('');
    setIcon('Package');
  };

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      eyebrow="Add an item"
      title="持ち物を追加"
      footer={
        <Button
          variant="primary"
          fullWidth
          size="lg"
          iconStart="Check"
          disabled={!label.trim() || saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave({
                childId,
                icon,
                label: label.trim(),
                labelEn: labelEn.trim(),
                note: note.trim() || null,
                order: nextOrder,
              });
              reset();
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? '保存中…' : '追加する · Add'}
        </Button>
      }
    >
      <Field label="なまえ · Name" hint="例：れんらくちょう">
        <TextInput value={label} onChange={setLabel} placeholder="持ち物の名前" autoFocus />
      </Field>
      <Field label="English">
        <TextInput value={labelEn} onChange={setLabelEn} placeholder="Communication book" />
      </Field>
      <Field label="メモ · Note" hint="どこにあるか、中身など">
        <TextInput value={note} onChange={setNote} placeholder="玄関 / 麦茶 など" />
      </Field>

      <Eyebrow style={{ marginTop: 4, marginBottom: 9 }}>アイコン · Icon</Eyebrow>
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', paddingBottom: 8 }}>
        {ICON_CHOICES.map((name) => (
          <Tag key={name} icon={name} selected={icon === name} onClick={() => setIcon(name)}>
            {''}
          </Tag>
        ))}
      </div>
    </Sheet>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <Eyebrow style={{ marginBottom: 6 }}>{label}</Eyebrow>
      {children}
      {hint ? (
        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 4 }}>{hint}</div>
      ) : null}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  inputMode,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  inputMode?: 'text' | 'decimal' | 'numeric';
}) {
  return (
    <input
      type={type}
      value={value}
      inputMode={inputMode}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      style={{
        width: '100%',
        height: 'var(--control-md)',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-strong)',
        background: 'var(--surface)',
        color: 'var(--text)',
        outline: 'none',
      }}
    />
  );
}
